/**
 * Daily bucketing of the canonical calls window, shared by the reception desk
 * KPIs (week-over-week deltas + sparklines) and the 14-day call-volume chart.
 * Everything here is derived from real `Call` rows — no fixtures.
 */

import type { Call } from '@hostesa/api-client';

const DAY_MS = 86_400_000;

export interface DailyBuckets {
  /** `YYYY-MM-DD` hotel-local day keys, oldest → newest (length = `days`). */
  keys: string[];
  /** Day-of-month labels aligned to `keys` (no leading zero). */
  labels: string[];
  /** All calls that day (inbound series). */
  all: number[];
  /** Completed calls that day (the desk's "booking captured" proxy). */
  completed: number[];
  /** Calls handed to a human that day (transferred series). */
  transferred: number[];
}

/** Hotel-local `YYYY-MM-DD` for a date (en-CA formats as ISO date). */
function dayKey(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/** The call's wall-clock instant, preferring `startedAt` then `createdAt`. */
function callDate(c: Call): Date | null {
  const raw =
    (typeof c.startedAt === 'string' && c.startedAt) ||
    (typeof c.createdAt === 'string' && c.createdAt) ||
    null;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Bucket calls into the last `days` calendar days (hotel timezone). */
export function bucketCallsByDay(
  calls: readonly Call[],
  timeZone: string,
  days = 14,
): DailyBuckets {
  const now = Date.now();
  const keys: string[] = [];
  const labels: string[] = [];
  const index = new Map<string, number>();
  for (let i = days - 1; i >= 0; i -= 1) {
    const key = dayKey(new Date(now - i * DAY_MS), timeZone);
    index.set(key, keys.length);
    keys.push(key);
    labels.push(String(Number(key.slice(8, 10))));
  }
  const all = new Array<number>(days).fill(0);
  const completed = new Array<number>(days).fill(0);
  const transferred = new Array<number>(days).fill(0);
  for (const c of calls) {
    const d = callDate(c);
    if (!d) continue;
    const i = index.get(dayKey(d, timeZone));
    if (i === undefined) continue;
    all[i] = (all[i] ?? 0) + 1;
    if (c.status === 'completed') completed[i] = (completed[i] ?? 0) + 1;
    if (c.status === 'human_active' || c.status === 'handoff_requested') {
      transferred[i] = (transferred[i] ?? 0) + 1;
    }
  }
  return { keys, labels, all, completed, transferred };
}

/** Sum of a numeric slice. */
function sum(values: readonly number[]): number {
  return values.reduce((acc, n) => acc + n, 0);
}

export interface WeekOverWeekDelta {
  value: string;
  tone: 'up' | 'down' | 'neutral';
}

/**
 * Week-over-week percentage change for a 14-day daily series (this week vs the
 * prior week). Returns `undefined` unless BOTH weeks carry data — a delta with
 * no baseline is a fabrication, so it is omitted rather than invented.
 */
export function weekOverWeekDelta(series: readonly number[]): WeekOverWeekDelta | undefined {
  if (series.length < 14) return undefined;
  const lastWeek = sum(series.slice(0, 7));
  const thisWeek = sum(series.slice(7, 14));
  if (lastWeek === 0 || thisWeek === 0) return undefined;
  const pct = Math.round(((thisWeek - lastWeek) / lastWeek) * 100);
  const tone: WeekOverWeekDelta['tone'] = pct > 0 ? 'up' : pct < 0 ? 'down' : 'neutral';
  return { value: `${pct > 0 ? '+' : ''}${pct}%`, tone };
}
